import { AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface PricingAdjustmentWarningProps {
  hasPricingAdjustments: boolean;
  adjustmentNotes?: string;
  pricingReliabilityScore?: number;
  variant?: "inline" | "badge" | "full";
  className?: string;
}

export function PricingAdjustmentWarning({ 
  hasPricingAdjustments, 
  adjustmentNotes, 
  pricingReliabilityScore, 
  variant = "inline",
  className = ""
}: PricingAdjustmentWarningProps) {
  if (!hasPricingAdjustments) return null;

  const getReliabilityColor = (score?: number) => {
    if (!score) return "text-yellow-400";
    if (score <= 25) return "text-red-400";
    if (score <= 50) return "text-orange-400";
    if (score <= 75) return "text-yellow-400";
    return "text-green-400";
  };

  const getReliabilityText = (score?: number) => {
    if (!score) return "Data Quality Issue";
    if (score <= 25) return "Low Reliability";
    if (score <= 50) return "Moderate Reliability"; 
    if (score <= 75) return "Fair Reliability";
    return "Good Reliability";
  };

  const reliabilityColor = getReliabilityColor(pricingReliabilityScore);
  const reliabilityText = getReliabilityText(pricingReliabilityScore);

  if (variant === "badge") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className={`${reliabilityColor} border-current ${className}`}>
              <AlertCircle className="w-3 h-3 mr-1" />
              {reliabilityText}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{adjustmentNotes || "Unit measurement or data quality issues may affect price comparison accuracy"}</p>
            {pricingReliabilityScore && (
              <p className="mt-1 text-xs">Reliability Score: {pricingReliabilityScore}/100</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (variant === "full") {
    return (
      <div className={`flex items-start p-3 bg-yellow-50 border border-yellow-200 rounded-lg ${className}`}>
        <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-medium text-yellow-800">Pricing Data Notice</div>
          <p className="text-yellow-700 mt-1">
            {adjustmentNotes || "This pricing data may have unit measurement or quality issues that affect comparison accuracy."}
          </p>
          {pricingReliabilityScore && (
            <p className="text-yellow-600 text-xs mt-1">
              Data Reliability: {pricingReliabilityScore}/100 - {reliabilityText}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default inline variant
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className={`flex items-center text-xs ${reliabilityColor} ${className}`}>
            <AlertCircle className="w-3 h-3 mr-1" />
            <span>Unit measurement issue</span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{adjustmentNotes || "Unit measurement or data quality issues may affect price comparison accuracy"}</p>
          {pricingReliabilityScore && (
            <p className="mt-1 text-xs">Reliability Score: {pricingReliabilityScore}/100</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}